<script setup>
import { ref } from 'vue'
import { DriverStore } from '../pinia/Driver.js'

// Vrai endpoint PayPal donation (cmd=_donations) — pas l'Orders API
const PAYPAL_BUSINESS = 'paypal@nightriderz.world'
const IPN_URL = 'https://api.nightriderz.world/donate-ipn.php'
const RETURN_URL = 'https://nightriderz.world/donate-return'

const Driver = DriverStore()

const amount = ref(5)
const amountInput = ref(5)

const emit = defineEmits(['opened'])

function setAmount(val) {
    amountInput.value = val
    amount.value = val
}

function applyCustomAmount() {
    const parsed = parseFloat(amountInput.value)
    if (!isNaN(parsed) && parsed >= 1) {
        amount.value = Math.round(parsed * 100) / 100
    }
}

function openDonation() {
    const userId = Driver.logged ? (Driver.user?.ID ?? '') : ''

    // Ouvre d'abord la popup vide, puis soumet le formulaire en POST vers cette popup.
    // Obligatoire : PayPal ignore notify_url si passé en GET, seulement supporté en POST.
    const popup = window.open('', 'paypal_donate', 'width=500,height=700')

    const params = {
        business: PAYPAL_BUSINESS,
        cmd: '_donations',
        item_name: 'Donation to NIGHTRIDERZ',
        amount: amount.value.toFixed(2),
        currency_code: 'EUR',
        no_shipping: '1',
        notify_url: IPN_URL,
        return: RETURN_URL,
        custom: String(userId),
    }

    const form = document.createElement('form')
    form.method = 'post'
    form.action = 'https://www.paypal.com/donate'
    form.target = 'paypal_donate'

    for (const [key, value] of Object.entries(params)) {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = value
        form.appendChild(input)
    }

    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)

    emit('opened')
}
</script>

<template>
    <div class="donation-wrapper">

        <!-- Sélection du montant -->
        <div class="amount-section">
            <div class="amount-presets">
                <button
                    v-for="preset in [2, 5, 10, 20]"
                    :key="preset"
                    class="preset-btn"
                    :class="{ active: amount === preset }"
                    @click="setAmount(preset)"
                >
                    €{{ preset }}
                </button>
            </div>

            <div class="custom-row">
                <span class="currency-symbol">€</span>
                <input
                    v-model.number="amountInput"
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Other amount"
                    class="custom-input"
                    @blur="applyCustomAmount"
                    @keyup.enter="applyCustomAmount"
                />
            </div>
        </div>

        <!-- Bouton PayPal -->
        <button class="paypal-btn" @click="openDonation">
            <img src="https://www.paypalobjects.com/webstatic/en_US/i/buttons/PP_logo_h_100x26.png" alt="PayPal" class="paypal-logo" />
            <span>Donate €{{ amount.toFixed(2) }}</span>
        </button>

    </div>
</template>

<style scoped>
.donation-wrapper {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    padding: 4px 0;
}

/* ---- Montants ---- */
.amount-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.amount-presets {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
}

.preset-btn {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 8px;
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
    padding: 10px 0;
    cursor: pointer;
    transition: all 0.15s ease;
}

.preset-btn:hover {
    background: rgba(239, 83, 80, 0.2);
    border-color: #ef5350;
}

.preset-btn.active {
    background: #ef5350;
    border-color: #ef5350;
    color: #fff;
}

/* ---- Champ custom ---- */
.custom-row {
    display: flex;
    align-items: center;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 8px;
    overflow: hidden;
    transition: border-color 0.15s;
}

.custom-row:focus-within {
    border-color: #ef5350;
}

.currency-symbol {
    padding: 0 12px;
    color: #aaa;
    font-size: 1rem;
    font-weight: 600;
    user-select: none;
}

.custom-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: #fff;
    font-size: 0.95rem;
    padding: 11px 12px 11px 0;
}

.custom-input::placeholder {
    color: #666;
}

/* ---- Bouton PayPal ---- */
.paypal-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: #FFC439;
    border: none;
    border-radius: 8px;
    padding: 13px 20px;
    cursor: pointer;
    font-weight: 700;
    font-size: 1rem;
    color: #111;
    transition: background 0.15s, transform 0.1s;
    width: 100%;
}

.paypal-btn:hover {
    background: #f0b429;
    transform: translateY(-1px);
}

.paypal-btn:active {
    transform: translateY(0);
}

.paypal-logo {
    height: 20px;
}
</style>
